/**
 * MessageWindow class controls the width, height and position of the chat window
 */
/* eslint-disable no-nested-ternary,no-mixed-operators,camelcase,no-param-reassign  */
/* global messagingApp */
import ScrollMonitor from "./ScrollMonitor";
import {
    Point,
    addOrRemoveFrameAria,
    attachListener,
    debounce,
    detachListener,
    getIphoneModel,
    getVisualViewportHeight,
    getZoom,
    isIE,
    isIOS,
    isChrome
} from "./Utils";
import Area from "./Area";
import Move from "./Move";
import DragResize from "./Drag";
import globalPubSubInstance from "../util/pubSubGlobal";
import { Constants, ORIENTATION } from "../model/Constants";
import { MessagingApp } from "../../model";
import { Timeout, isAndroid } from "../util/Misc";

declare const messagingApp: any;

/**
 * @class MessageWindow
 */
export default class MessageWindow {
    frame: HTMLIFrameElement;

    area: Area;

    resizeHandler: any;

    minimizedCount: number;

    chatActive: boolean;

    scrollHandler: typeof MessageWindow.prototype.scrollDebouncedEvent;

    keyboardScrollHandler: typeof MessageWindow.prototype.keyboardScrollDebouncedEvent;

    moveScrollHandler: typeof MessageWindow.prototype.touchMoveScrollHandler;

    iphoneModel: any;

    static bottomDelta: number;

    static ipadChromeBottomDelta: number;

    static minFrameHeight: number;

    static screenOffset: any;

    doc: any;

    ciApp: any;

    mApp: any;

    theme: any;

    minimized: any;

    currentOrientation: any;

    metaTag: any;

    keyboardUpTimer: NodeJS.Timeout;

    scrollState: string;

    scrollStates = {
        beforeScroll: "BEFORE_SCROLL",
        scrolledToTop: "SCROLLED_TO_TOP",
        willScrollSlightlyUp: "WILL_SCROLL_SLIGHTLY_UP",
    };

    forceScrollTimer: any;

    isVirtualKeyboardPresent: any;

    scrolled: number;

    kbHiddenTimer: any;

    timeout: any;

    messagingTopBarEl: any;

    static minWidth: any;

    static minHeight: any;

    /**
     *
     * @param {MessagingApp} messagingApp
     */
    constructor(messagingApp: MessagingApp) {
        this.frame = messagingApp.getStage();
        this.area = null;
        this.resizeHandler = debounce(this.onBrowserResize.bind(this), 100);
        this.minimizedCount = 0;
        this.chatActive = false;

        if (isIOS()) {
            this.scrollHandler = debounce(this.scrollDebouncedEvent.bind(this), 100);
            this.keyboardScrollHandler = debounce(this.keyboardScrollDebouncedEvent.bind(this), 100);
            this.moveScrollHandler = debounce(this.touchMoveScrollHandler.bind(this), 100);
            this.iphoneModel = getIphoneModel();
        }

        const skinConfig = messagingApp.getUIConfig().skinConfig;
        MessageWindow.bottomDelta = 0;
        MessageWindow.minFrameHeight = 0;
        MessageWindow.ipadChromeBottomDelta = 0;

        if (skinConfig) {
            MessageWindow.bottomDelta = skinConfig.bottomOffset || 0;
            MessageWindow.screenOffset = skinConfig.fullScreenOffset || 0;
            MessageWindow.ipadChromeBottomDelta = skinConfig.ipadChromeBottomDelta || 0;
        }

        this.doc = this.frame.ownerDocument;
        this.ciApp = messagingApp.getCIAppInstance();
        this.mApp = messagingApp;
    }

    /**
     * render the frame either with the position from theme or the one set in bootstrap config
     */
    render(theme: { minimized: { state: any; count: number; }; embedded: any; }, callback?: any): void {
        // let __height;
        /** @type {number} */
        // let __width;
        /** @type {Window} */
        // let __winParent = window.parent;
        /** @type {com.inq.utils.Area} */
        const vp = ScrollMonitor.getViewport();

        this.theme = theme;
        ScrollMonitor.setDeviceType(this.theme.deviceType);

        if (!this.minimized && theme && theme.minimized && theme.minimized.state) {
            this.minimized = true;
            this.minimizedCount = theme.minimized.count;

            if (messagingApp.getPluginInstance()) {
                messagingApp.getPluginInstance().setMinimized(this.minimizedCount);
            }
            return;
        }

        if (theme.embedded) {
            this.frame.classList.add("embedded");
        } else if (this.mApp.getSDKInstance().isPersistentWindow()) {
            this.frame.classList.add("persistent-chat");
        } else if (this.isPhone()) {
            this.chatActive = true;
            this.currentOrientation = this.getOrientation(vp);
            this.addViewPort();
            /**
             *IOS safari we need to turn off the user scalaing option to no, it is under impression that there is already a viewport tag present
             */
            if (isIOS()) {
                detachListener(window.top, "scroll", this.scrollHandler);
                attachListener(window.top, "scroll", this.scrollHandler);
                this.doc.body.classList.add("chatopen");
                window.top.scrollTo(0, 1);
            } else {
                this.doc.documentElement.style.overflow = "hidden";
            }

            // breaking the event loop for getting correct vp x,y
            setTimeout(function () {
                this.resizeForPhone(vp);
            }.bind(this), 100);
        } else {
            /**
             * The given value of MessageWindow.ipadChromeBottomDelta property is used to inject a buffer between chat window Iframe and its parent nuanMessagingFrame div.
             * This way chat window will be away from the bottom by the given value. PS can optionally give  background color to nuanMessagingFrame div so it not appear to be transparent.
             * In full screen mode below delta value won't be available, to add a buffer in full sreen mode we have added a new class to the nuanMessaging div. PS can use this class to set a buffer
             */
            if (this.isChromeOnIPAD() && MessageWindow.ipadChromeBottomDelta > 0) {
                MessageWindow.bottomDelta = MessageWindow.ipadChromeBottomDelta;
            }

            const config = messagingApp.config;
            this.area = new Area();
            this.area.width = config.frameW ? config.frameW : this.theme.skinWidth;
            this.area.height = (config.frameH ? config.frameH : this.theme.skinHeight + this.theme.titleBarHeight) + MessageWindow.bottomDelta;

            /*
             * some reason requestAnimationFrame is taking time to fire its callback,probably some thing in client code causing drop in frame per second
             * or blocking the layout and paint job
             */
            setTimeout(() => {
                this.setWH();
                this.resizeWindow();
                this.onBrowserResize(undefined, callback);
            }, 25);
        }

        if (window.top.visualViewport) {
            detachListener(window.top.visualViewport, "resize", this.resizeHandler);
            attachListener(window.top.visualViewport, "resize", this.resizeHandler);
        }
        detachListener(window.top, "resize", this.resizeHandler);
        attachListener(window.top, "resize", this.resizeHandler);
        const chatFrame = this.frame.querySelector("iframe");
        addOrRemoveFrameAria(false, chatFrame);
        chatFrame.classList.remove("animation-frame");
        Timeout.clear("ChatMinimizeAnimationTimeout");
    }

    /**
     * return the orienation of the device,
     * On Android best way to find orientation is screen.availHeight and screen.availWidth because virtual keyboard resizes the viewport width and height
     * On IOS it is best to get viewport as viewport width and height never changes with respect keyboard display
     * @param vp
     * @return {string}
     */
    getOrientation(vp: Area): string {
        if (isIOS()) {
            return vp.height > vp.width ? ORIENTATION.PORTRAIT : ORIENTATION.LANDSCAPE;
        }
        return window.screen.availHeight > window.screen.availWidth ? ORIENTATION.PORTRAIT : ORIENTATION.LANDSCAPE;
    }

    /**
     *
     */

    resizeForPhone(): void {
        if (this.isMinimized() || !this.chatActive) {
            return;
        }

        this.area = ScrollMonitor.getViewport();
        this.setWH();
        this.updatePosition();
    }

    createNewMetaOfViewPort(content: string): void {
        if(content){
            this.metaTag = this.doc.createElement("meta");
            this.metaTag.name = "viewport";
            this.metaTag.content = content;
            this.doc.getElementsByTagName("head")[0].appendChild(this.metaTag);
        }
    }

    /*
     * New updates in chrome version 108 has updated the handling of viewport. (https://developer.chrome.com/blog/viewport-resize-behavior)
     * We need to check height from visualViewport rather than depending on innerHeight.
     * For Chrome Android we need to add "interactive-widget=resizes-content" in viewport meta tag to revert back to old behaviour
    */
    addViewPort(): void {
        let metaContent = '';
        if (isIOS()) {
            metaContent = "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0";
        } else if (isAndroid() && isChrome()) {
            metaContent = "width=device-width, initial-scale=1.0, interactive-widget=resizes-content";
        }
        this.createNewMetaOfViewPort(metaContent);
    }

    /**
     * In IOS Safari if input already has focus through setting programatically, user click on input doesn't fire focus event again.
     * Fall back logic to resize chat window when focus event is not fired by safari
     */
    keyboardScrollDebouncedEvent(): void {
        detachListener(window.top, "scroll", this.keyboardScrollHandler);
        this.keyboardUpTimer = setTimeout(function () {
            this.onVirtualKeyboardOpen();
        }.bind(this), 100);
    }

    scrollDebouncedEvent(e?: Event): void {
        this.resizeForPhone();

        if (this.scrollState == this.scrollStates.willScrollSlightlyUp) {
            this.scrollState = this.scrollStates.beforeScroll;

            setTimeout(() => {
                this.isVirtualKeyboardPresent = true;
                window.top.scrollTo(0, 0);
                setTimeout(() => {
                    if (this.scrollState === this.scrollStates.scrolledToTop) {
                        this.scrollHandler();
                    }
                }, 500);
                this.scrollState = this.scrollStates.scrolledToTop;
            }, 200);
            return;
        }

        if (this.isVirtualKeyboardPresent) {
            this.scrollState = this.scrollStates.beforeScroll;
            const vp = ScrollMonitor.getViewport();
            const chatDiv = this.frame.querySelector(`#${this.mApp.config.frameID}`) as HTMLDivElement;
            let keyboardH: number;

            if (window.parent.visualViewport) {
                //Calculate virtual keyboard height using visual viewport
                keyboardH = window.parent.innerHeight - getVisualViewportHeight();

                if (keyboardH < 10) {
                    /**
                     * Programatic focus to input may not display a keyboard, creating a scroll listener
                     */
                    this.onForcedInputFocus();
                }
            } else {
                keyboardH = vp.height > vp.width ? (this.area.height * (MessageWindow.keyBoardValuePort()[this.iphoneModel] || 0.43)) : this.area.height * 0.60;
            }

            this.frame.style.height = null;
            this.frame.style.bottom = `${keyboardH}px`;

            const iFrameStyle = getComputedStyle(this.frame);
            const viewportHeight = vp.height;
            const iFramePaddingTop = parseInt(iFrameStyle.paddingTop, 10);
            const iFramePaddingBottom = parseInt(iFrameStyle.paddingBottom, 10);
            const iFrameVerticalPadding = iFramePaddingTop + iFramePaddingBottom;
            chatDiv.style.height = `${viewportHeight - iFrameVerticalPadding}px`;
            this.reLayout();
            this.scrolled = window.top.pageYOffset;
            detachListener(window.top, "scroll", this.scrollHandler);
            detachListener(window.top, "scroll", this.moveScrollHandler);
            attachListener(window.top, "scroll", this.moveScrollHandler);

            if (keyboardH > 0) {
                globalPubSubInstance.publish(Constants.VIRTUAL_KEYBOARD_DISPLAYED, e);
            }
        }
    }

    touchMoveScrollHandler(e: any): void {
        e.preventDefault();
        e.stopPropagation();
        window.top.scrollTo(0, this.scrolled);
    }

    onVirtualKeyboardOpen(): void {
        /*
         * this.isVirtualKeyboardPresent = false;
         * embedded chat is embedded to the page , so client expects it should not resize when keyboard appears
         */
        if (this.theme && this.theme.embedded) {
            return;
        }

        if (this.isIpad()) {
            // todo will implement for tablet resizing
        } else {
            detachListener(window.top, "scroll", this.scrollHandler);
            attachListener(window.top, "scroll", this.scrollHandler);
            this.clearKeyboardTimer();

            this.scrollState = this.scrollStates.willScrollSlightlyUp;
        }
    }

    /**
     * Listener for window scroll event due to input focus
     */
    onForcedInputFocus(): void {
        attachListener(window.top, "scroll", this.keyboardScrollHandler);
        this.clearKeyboardTimer();
        this.keyboardUpTimer = setTimeout(() => {
            if (this.scrollState === this.scrollStates.beforeScroll && this.chatActive) {
                this.keyboardScrollDebouncedEvent();
            } else {
                this.isVirtualKeyboardPresent = false;
                detachListener(window.top, "scroll", this.keyboardScrollHandler);
                detachListener(window.top, "scroll", this.moveScrollHandler);
            }
        }, 200);
    }

    clearKeyboardTimer(): void {
        if (this.keyboardUpTimer) {
            clearTimeout(this.keyboardUpTimer);
        }
    }

    onVirtualKeyboardHidden(): void {
        if (this.theme && this.theme.embedded) {
            return;
        }

        if (this.isIpad()) {
            // todo will implement for tablet resizing
        } else {
            if (this.isVirtualKeyboardPresent) {
                if (this.kbHiddenTimer) {
                    clearTimeout(this.kbHiddenTimer);
                }

                this.kbHiddenTimer = setTimeout(function () {
                    const vp = ScrollMonitor.getViewport();
                    this.isVirtualKeyboardPresent = false;
                    this.frame.style.bottom = null;
                    this.resizeForPhone(vp);
                    this.kbHiddenTimer = null;
                }.bind(this), 250);
            }

            detachListener(window.top, "scroll", this.moveScrollHandler);
        }
    }

    /**
     * Responsively adjust the position and width height of chat window when the page resize
     */
    onBrowserResize(e?: Event, callback?: Function): void {
        if (this.timeout) {
            window.cancelAnimationFrame(this.timeout);
        }

        this.timeout = window.requestAnimationFrame(() => {
            if (this.mApp.getSDKInstance().isPersistentWindow() || this.theme.embedded) {
                // eslint-disable-line no-empty
            } else if (this.isPhone()) {
                const vp = ScrollMonitor.getViewport();
                const orientation = this.getOrientation(vp);

                if (this.currentOrientation !== orientation) {
                    this.currentOrientation = orientation;
                    this.resizeForPhone();

                    if (isIOS() && this.isVirtualKeyboardPresent) {
                        this.handleIOSVirtualKeyboardWhenResized(vp, e);
                        return;
                    }

                    if (isIOS()) {
                        setTimeout(function () {
                            window.top.scrollTo(0, 0);
                        }, 200);
                    }

                    globalPubSubInstance.publish(Constants.WINDOW_RESIZED, e);
                } else {
                    if (isIOS() && this.isVirtualKeyboardPresent) {
                        this.resizeForPhone();
                        globalPubSubInstance.publish(Constants.WINDOW_RESIZED, e);
                        this.handleIOSVirtualKeyboardWhenResized(vp, e);
                    } else if (isIOS()) {
                        /*
                         * it is very necessary to break the loop this so that viewport gives the accurate value, This case is called when user started a chat in zoomed in page and later he zoomed out
                         * if zoom level is nearly equal to device independed width then resize to  viewport
                         */
                        setTimeout(function () {
                            this.resizeForPhone(vp);
                            globalPubSubInstance.publish(Constants.WINDOW_RESIZED, e);
                        }.bind(this), 200);
                    } else {
                        this.resizeForPhone();
                        globalPubSubInstance.publish(Constants.WINDOW_RESIZED, e);
                    }
                }
            } else {
                const heightOffsetVal = MessageWindow.screenOffset;
                const chatWindowHeight = parseInt(this.frame.style.height, 10);
                const scrollHeight = ScrollMonitor.getScrollHeight();
                const isChatWindowBigger = (scrollHeight > chatWindowHeight
                    && heightOffsetVal === 0);
                const isChatWindowBiggerWithOffset = ((heightOffsetVal > 0
                    && scrollHeight - heightOffsetVal) > chatWindowHeight);
                const chromeIPADClassName = this.getChromeIPADClassName();

                if (isChatWindowBigger
                    || isChatWindowBiggerWithOffset) {
                    if (this.frame.classList.contains("adjust-height")) {
                        if (chromeIPADClassName !== "") {
                            this.frame.classList.remove("adjust-height", chromeIPADClassName);
                        } else {
                            this.frame.classList.remove("adjust-height");
                        }

                        this.ciApp.toggleFullScreen(false);
                    }

                    this.resizeWindow(callback);

                    this.area.height = parseInt(this.frame.style.height, 10);
                } else {
                    if (!this.frame.classList.contains("adjust-height")) {
                        if (chromeIPADClassName !== "") {
                            this.frame.classList.add("adjust-height", chromeIPADClassName);
                        } else {
                            this.frame.classList.add("adjust-height");
                        }

                        this.ciApp.toggleFullScreen(true);
                    }

                    this.area.height = ScrollMonitor.getViewport().height;
                }

                if (ScrollMonitor.getScrollWidth() > parseInt(this.frame.style.width, 10)) {
                    if (this.frame.classList.contains("adjust-width")) {
                        this.frame.classList.remove("adjust-width");
                    }

                    this.resizeWindow(callback);

                    this.area.width = parseInt(this.frame.style.width, 10);
                } else {
                    if (!this.frame.classList.contains("adjust-width")) {
                        this.frame.classList.add("adjust-width");
                    }

                    this.area.width = ScrollMonitor.getViewport().width;
                }
            }
        });
    }

    /**
     * Display Virtual keyboard if virtual keyboard displayed variable is true and a resize or orientation event happens
     * @param {Object} vp
     * @param {Event} e
     */
    handleIOSVirtualKeyboardWhenResized(vp: { width: number; height: number; }, e: any) {
        if (vp.width > vp.height) {
            this.scrollDebouncedEvent(e);
        } else {
            this.scrollState = this.scrollStates.willScrollSlightlyUp;
            detachListener(window.top, "scroll", this.scrollHandler);
            attachListener(window.top, "scroll", this.scrollHandler);
        }
    }

    setWH() {
        const chatDiv = this.frame.querySelector(`#${this.mApp.config.frameID}`) as HTMLDivElement;
        const st = getComputedStyle(this.frame);
        this.frame.style.width = `${this.area.width}px`;

        chatDiv.classList.add("enter");
        chatDiv.style.width = `${this.area.width - (parseInt(st.paddingLeft, 10) + parseInt(st.paddingRight, 10))}px`;
        this.frame.style.height = `${this.area.height}px`;

        if (this.isPhone()) {
            chatDiv.style.height = (`${this.area.height - (parseInt(st.paddingTop, 10) + parseInt(st.paddingBottom, 10))}px`);
        } else {
            chatDiv.style.height = (`${this.area.height - MessageWindow.bottomDelta - (parseInt(st.paddingTop, 10) + parseInt(st.paddingBottom, 10))}px`);
        }
    }

    setPersistentChatWH() { // eslint-disable-line class-methods-use-this

    }

    /**
     * determine the size , position of chat widnow, reuse the position even after page reload
     */

    resizeWindow(callback?: any): void {
        // var vp = ScrollMonitor.getViewport();
        const __width = this.area.width;
        const __height = this.area.height;
        let position: Point;

        if (this.theme && this.theme.skinRightSaved != -1 && this.theme.skinBottomSaved != -1) {
            position = new Point();
            position.p1 = this.theme.skinRightSaved;
            position.p2 = this.theme.skinBottomSaved;
        } else {
            const config = messagingApp.config;

            position = MessageWindow.calcChatLocation(
                __width,
                __height,
                this.theme ? this.theme.skinLeft : 0, /** its is bottom for websdk */
                this.theme ? this.theme.skinTop : 0,
                config.framePos ? config.framePos : this.theme.skinPosition
            );
        }

        // if (__height < vp.height && __width < vp.width) {
        position.p1 = MessageWindow.setFixedRPosition(position.p1, __width);
        position.p2 = MessageWindow.setFixedBPosition(position.p2, __height);

        // }
        this.area.setBRPosition(position.p1, position.p2, __width, __height);
        this.updatePosition();

        // Scroll to bottom after opening the minimized chat
        if (typeof (callback) !== "undefined") {
            callback();
        }
    }

    /**
     * handle the drag reposition of chat window
     */

    moveStage(left: number, top: number) {
        const right = ScrollMonitor.getScrollWidth() - (left + this.area.width);
        const bottom = ScrollMonitor.getScrollHeight() - (top + this.area.height);
        this.area.setBRPosition(right, bottom, this.area.width, this.area.height);
        if (this.theme.skinRightSaved != right || this.theme.skinBottomSaved != bottom) {
            messagingApp.getSDKInstance().saveWindowPosition(this.area.b, this.area.r);
            this.updatePosition();
        }

        this.theme.skinRightSaved = right;
        this.theme.skinBottomSaved = bottom;
    }

    /**
     * handle the drag bar element , drag bar width and height is set in portal
     */

    addDragElement(theme: { embedded: any; }, parent: any) {
        if (!this.isPhone() && theme && !this.mApp.getSDKInstance().isPersistentWindow() && !theme.embedded) {
            /*
             * let messagingTopBarEl = this.messagingTopBarEl = new Element("messagingMessagingTopBar");
             * MessageWindow.clearImage = theme.imagePath + "clear.gif";
             * messagingTopBarEl.addCssText("position:absolute; height: 1px; width: 1px; left: 0px; top: 0px; borderThickness:0px");
             * messagingTopBarEl.setInnerHTML("<input type=\"image\" src=\"" + MessageWindow.clearImage + "\" style=\"position:static; cursor: default;\" alt=\"\" tabindex=\"-1\"/>");
             * messagingTopBarEl.setStyle("height","100%");
             * messagingTopBarEl.setStyle("width","100%");
             * // messagingTopBarEl.setStyle("pointerEvents","none");
             */
            const conf = messagingApp.getUIConfig();

            /*
             * if( conf && conf.topBarConfig && conf.topBarConfig.topBarSettings) {
             *  var ts = conf.topBarConfig.topBarSettings;
             *
             *  if(ts.adjustDragBarLeft) messagingTopBarEl.setStyle("left",ts.adjustDragBarLeft);
             *  if(ts.adjustDragBarWidth) messagingTopBarEl.setStyle("width",ts.adjustDragBarWidth);
             *  if(ts.adjustDragBarHeight) messagingTopBarEl.setStyle("height",ts.adjustDragBarHeight);
             *
             * }
             */
            /*
             * if(firstChild) {
             *  messagingTopBarEl.prependToParent(parent);
             * } else {
             *  messagingTopBarEl.appendToParent(parent || this.frame);
             * }
             */

            /*
             *var resizeCorner = this.inqResizeCorner = new Element("nuanResizeCorner");
             *resizeCorner.addCssText("position: absolute; right: 0px; bottom: 0px; cursor:se-resize; borderThickness:0px");
             *
             *resizeCorner.setAttribute("alt", "");
             *resizeCorner.setAttribute("aria-hidden", "true");
             *resizeCorner.setAttribute("tabindex", "-1");
             *
             *
             *resizeCorner.setInnerHTML("<input type=\"image\" src=\"" + theme.imagePath + "ResizeCorner.gif" + "?rand=" + Math.random() + "\""
             *        + "style=\"position: absolute; top:0px; left:0px; height: 100%; width: 100%; bottom: 0px; right: 0px; display: block; cursor:se-resize;\" tabindex=\"-1\"/>");
             *
             *
             *resizeCorner.setStyle("width", MessageWindow.resizeArea.width + "px");
             *resizeCorner.setStyle("height", MessageWindow.resizeArea.height + "px");
             *resizeCorner.appendToParent(window.document.body);
             */
            // this.updateDragResizePos();
            Move.setDragable(conf.appConfigProps.dragMode, parent || this.frame);

            // Resize.setResizable();
        }
    }

    /*
     * MessageWindow.prototype.updateDragResizePos = function() {
     * this.messagingTopBarEl.setStyle("left",this.area.x + "px");
     * this.messagingTopBarEl.setStyle("top", this.area.y + "px");
     *
     *  this.inqResizeCorner.setStyle("left", this.area.x + this.area.width - MessageWindow.resizeArea.width + "px");
     *  this.inqResizeCorner.setStyle("top", this.area.y + this.area.height - MessageWindow.resizeArea.height + "px");
     *  this.inqResizeCorner.setStyle("zIndex", 9999999 + 1);
     *
     * }
     */

    /*
     *MessageWindow.getDefaultResizeArea = function() {
     *    var a;
     *    if (isMobile()) {
     *        a = new Area(0, 0, 32, 32);
     *    } else {
     *        a = new Area(0, 0, 16, 16);
     *    }
     *    return a;
     *}
     *
     *
     *MessageWindow.resizeArea = MessageWindow.getDefaultResizeArea();
     */

    updatePosition() {
        this.reLayout();
        globalPubSubInstance.publish("MessagingWindow.updatePosition", this.area);
    }

    reLayout() {
        if (this.area.isXYAxisPos()) {
            this.frame.style.left = `${this.area.x}px`;
            /**
             * I haven't been able to figure out why yet, but on both iOS and Android we set the
             * "nuanMessagingFrame" div, which is the IFrame's parent, to a "fixed" position. Yet,
             * on Android, the "top" value is relative to the viewport, and on iOS it's relative to
             * the document.
             */
            this.frame.style.top =
                this.isPhone() && isAndroid() ? `0px` : `${this.area.y}px`;
        } else {
            this.frame.style.right = `${this.area.r}px`;
            this.frame.style.bottom = `${this.area.b}px`;
            this.frame.style.left = null;
            this.frame.style.top = null;
        }
    }

    /**
     * set the visibility of frame to none when minimize state is fired
     */
    minimize(): void {
        this.setVisible(false);
        this.minimized = true;
    }

    isMinimized(): any {
        return this.minimized;
    }

    isChatActive(): boolean {
        return this.chatActive;
    }

    setVisible(visible: boolean): void {
        if (visible) {
            const isRestore = this.minimized;
            this.minimized = false;
            this.theme.minimized = {};
            this.render(this.theme, () => {
                if (isRestore) {
                    globalPubSubInstance.publish(Constants.CHAT_WINDOW_RESTORED, null);
                }
            });
            this.minimizedCount = 0;
            this.ciApp.onResumeFrame();
        } else {
            if (window.top.visualViewport) {
                detachListener(window.top.visualViewport, "resize", this.resizeHandler);
            }
            detachListener(window.top, "resize", this.resizeHandler);

            if (this.messagingTopBarEl) {
                const dragInst = this.messagingTopBarEl.div[DragResize.INSTANCE_LABEL];

                if (dragInst) {
                    dragInst.clear();
                }

                this.messagingTopBarEl.delete();
            } else {
                const deviceType = this.mApp.getSDKInstance().getDeviceType();
                detachListener(window.top, "scroll", this.scrollHandler);
                detachListener(window.top, "scroll", this.moveScrollHandler);
                detachListener(window.top, "scroll", this.keyboardScrollHandler);

                if (this.metaTag) {
                    this.metaTag.parentElement.removeChild(this.metaTag);
                }

                if (isIOS()) {
                    this.doc.body.classList.remove("chatopen");
                } else if (["Phone", "Tablet"].includes(deviceType)) {
                    // Only add for devices with keyboard, as our scrollHandler should work correctly
                    this.doc.documentElement.style.overflow = "scroll";
                }
            }

            const chatDiv: HTMLElement = this.frame.querySelector(`#${this.mApp.config.frameID}`);

            if (this.ciApp.uiConfig.appConfigProps.animateOnMinimizeOrClose) {
                chatDiv.classList.add("animation-frame");
                Timeout.set("ChatMinimizeAnimationTimeout", () => {
                    this.minimizeMainFrame(chatDiv);
                }, 500);
            } else {
                this.minimizeMainFrame(chatDiv);
            }

            this.chatActive = false;
        }
    }

    clear(): void {
        this.setVisible(false);
        this.frame.style.bottom = null;
        this.frame.style.right = null;
        this.ciApp.onDestroyFrame();
    }

    minimizeMainFrame(chatDiv): void {
        this.frame.style.height = "1px";
        this.frame.style.width = "1px";
        chatDiv.classList.remove("enter", "animation-frame");
        chatDiv.style.width = null;
        chatDiv.style.height = null;
        this.frame.classList.remove("embedded", "adjust-height", "adjust-width");

        if (this.kbHiddenTimer) {
            clearTimeout(this.kbHiddenTimer);
        }

        addOrRemoveFrameAria(true, chatDiv);
    }

    static setFixedRPosition(r: any, w: any): number {
        return MessageWindow.FixR(r, w);
    }

    static setFixedBPosition(t: any, h: any): number {
        return MessageWindow.FixB(t, h);
    }

    static FixR(r: number, w: number): number {
        /** @type {number} */
        let right = r < 0 ? 0 : r;

        /** @type {number} */
        const left = right + w;

        /** @type {number} */
        const xMax = ScrollMonitor.getScrollWidth();

        if (left > xMax && right != 0) {
            right = xMax - w;
        }

        if (right < 0) {
            right = 0;
        }
        return right;
    }

    static FixB(b: number, h: number): number {
        /** @type {number} */
        let bottom = b < 0 ? 0 : b;

        /** @type {number} */
        const scrollHeight = ScrollMonitor.getScrollHeight();

        /*
         * Make sure that the Y position allows the
         * chat to be totally visible in the window.
         * 1) get the height of the scroll area (viewport)
         * 2) subtract the height to yield the maximum absolute position
         * 3) adjust if greater than.
         */

        /** @type {number} */
        const yMax = scrollHeight - h;

        if (bottom > yMax && bottom != 0) {
            bottom = yMax;
        }

        if (bottom < 0) {
            bottom = 0;
        }
        return bottom;
    }

    static calcChatLocation(W: number, H: number, X: number, Y: number, location: string): Point {
        /** @type {number} */
        let right = 0;

        /** @type {number} */
        let bottom = 0;

        /** @type {number} */
        const scrollBarWidth = 0;// isIE() ? 0 : ScrollMonitor.getScrollBarWidth();
        /** @type {boolean} */
        const isRightScrollBar = ScrollMonitor.hasRightScrollBar();

        /** @type {boolean} */
        const isBottomScrollBar = ScrollMonitor.hasBottomScrollBar();

        /** @type {number} */
        let inq_scroll_width;

        /** @type {number} */
        let inq_scroll_height;

        if (isIE()) {
            const doc = window.parent.document;
            inq_scroll_height = doc.documentElement.clientHeight;

            if (inq_scroll_height == 0) {
                inq_scroll_height = doc.body.clientHeight;
            }

            inq_scroll_width = doc.documentElement.clientWidth;

            if (inq_scroll_width == 0) {
                inq_scroll_width = doc.body.clientWidth;
            }
        } else {
            inq_scroll_height = window.parent.innerHeight;
            inq_scroll_width = window.parent.innerWidth;
        }

        if (W < MessageWindow.minWidth) {
            W = MessageWindow.minWidth;
        }

        if (H < MessageWindow.minHeight) {
            H = MessageWindow.minHeight;
        }

        switch (location.toUpperCase()) {
            case "UPPER CENTER":

            case "UPPER_CENTER":

            case "TOP_CENTER":
                bottom = inq_scroll_height - H;
                right = inq_scroll_width / 2 - W / 2;
                break;

            case "UPPER_RIGHT":

            case "TOP_RIGHT":
                bottom = inq_scroll_height - H;
                right = (isRightScrollBar ? scrollBarWidth : 0);
                break;

            case "UPPER_LEFT":

            case "TOP_LEFT":
                bottom = inq_scroll_height - H;
                right = inq_scroll_width - W;
                break;

            case "CENTER_LEFT":
                bottom = inq_scroll_height / 2 - H / 2;
                right = inq_scroll_width - W;
                break;

            case "CENTER_RIGHT":
                bottom = inq_scroll_height / 2 - H / 2;
                right = (isRightScrollBar ? scrollBarWidth : 0);
                break;

            case "LOWER_LEFT":

            case "BOTTOM_LEFT":
                bottom = (isBottomScrollBar ? scrollBarWidth : 0);
                right = inq_scroll_width - W;
                break;

            case "LOWER_CENTER":

            case "BOTTOM_CENTER":
                bottom = (isBottomScrollBar ? scrollBarWidth : 0);
                right = inq_scroll_width / 2 - W / 2;

                break;

            case "LOWER_RIGHT":

            case "BOTTOM_RIGHT":
                right = (isRightScrollBar ? scrollBarWidth : 0);
                bottom = (isBottomScrollBar ? scrollBarWidth : 0);

                break;

            case "POP_UNDER_CENTER":
                right = inq_scroll_width / 2 - W / 2;
                bottom = inq_scroll_height / 2 - H;
                break;

            case "ABSOLUTE":

            case "FIXED":
                right = X;
                bottom = Y;
                break;

            case "RELATIVE":
                right = X;
                bottom = Y;
                break;

            case "CENTER":
                right = inq_scroll_width / 2 - W / 2;
                bottom = inq_scroll_height / 2 - H / 2;
                break;

            // no default
        }

        if (right == null || right < 0) {
            right = 0;
        }

        if (bottom == null || bottom < 0) {
            bottom = 0;
        }

        /* return {top: top, left: left } ; */
        return new Point(right | 0, bottom | 0); // eslint-disable-line no-bitwise
    }

    isPhone(): boolean {
        return this.theme.deviceType == "Phone";
    }

    isTablet(): boolean {
        return this.theme.deviceType == "Tablet";
    }

    isChromeOnIPAD(): boolean {
        return this.isTablet() && isChrome() && isIOS();
    }

    getChromeIPADClassName(): "chrome-ipad" | "" {
        return this.isChromeOnIPAD() ? "chrome-ipad" : "";
    }

    isMobile(): boolean {
        return this.isPhone() || this.isTablet();
    }

    static keyBoardValuePort(): { iPhoneX: number; } {
        return { iPhoneX: 0.45 };
    }

    isIpad(): boolean {
        return this.isTablet() && isIOS();
    }
}
